import { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { collection, addDoc, onSnapshot, query, where, orderBy, updateDoc, doc, serverTimestamp } from "firebase/firestore";

const STATUS = {
  pending: { color: "#F59E0B", bg: "#F59E0B18", label: "Pending" },
  approved: { color: "#22C55E", bg: "#22C55E18", label: "Approved" },
  denied: { color: "#EF4444", bg: "#EF444418", label: "Denied" },
};

const LEAVE_TYPES = ["Annual Leave", "Sick Leave", "Emergency Leave", "Training", "Other"];

const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export default function LeaveRequests({ userRole }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: "Annual Leave", from: "", to: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const isAdmin = userRole === "admin" || userRole === "supervisor" || userRole === "head_of_security";

  useEffect(() => {
    const q = isAdmin
      ? query(collection(db, "leave_requests"), where("tenant_id", "==", "osecurity-ltd"), orderBy("created_at", "desc"))
      : query(collection(db, "leave_requests"), where("uid", "==", auth.currentUser?.uid || ""), orderBy("created_at", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [isAdmin]);

  const submitRequest = async () => {
    if (!form.from || !form.to) { alert("Please select dates"); return; }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "leave_requests"), {
        uid: auth.currentUser?.uid,
        officer_name: auth.currentUser?.email?.split("@")[0] || "Officer",
        officer_email: auth.currentUser?.email,
        type: form.type,
        from: new Date(form.from),
        to: new Date(form.to),
        reason: form.reason,
        status: "pending",
        tenant_id: "osecurity-ltd",
        created_at: serverTimestamp(),
      });
      setShowAdd(false);
      setForm({ type: "Annual Leave", from: "", to: "", reason: "" });
    } catch (err) {
      alert("Error: " + err.message);
    }
    setSubmitting(false);
  };

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, "leave_requests", id), {
      status,
      reviewed_by: auth.currentUser?.email,
      reviewed_at: serverTimestamp(),
    });
  };

  const days = (from, to) => {
    const d = Math.ceil((new Date(to?.toDate ? to.toDate() : to) - new Date(from?.toDate ? from.toDate() : from)) / (1000*60*60*24)) + 1;
    return d > 0 ? d : 0;
  };

  const S = {
    page: { padding: "0 24px 24px", color: "#F8FAFC" },
    hdr: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 0 16px" },
    btn: (c, o) => ({ background: o ? "transparent" : c, color: o ? c : "#fff", border: `1px solid ${c}`, padding: "7px 16px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 }),
    card: { background: "#0A1628", border: "1px solid #1E3A5F", borderRadius: 8, padding: 16, marginBottom: 12 },
    badge: (s) => ({ background: STATUS[s]?.bg || "#6B728018", color: STATUS[s]?.color || "#9CA3AF", padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }),
    inp: { background: "#0A1628", border: "1px solid #1E3A5F", color: "#F8FAFC", padding: "8px 12px", borderRadius: 6, fontSize: 12, width: "100%" },
    sel: { background: "#0A1628", border: "1px solid #1E3A5F", color: "#F8FAFC", padding: "8px 12px", borderRadius: 6, fontSize: 12, width: "100%" },
  };

  const pending = requests.filter(r => r.status === "pending").length;

  return (
    <div style={S.page}>
      <div style={S.hdr}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 1 }}>🏖️ LEAVE REQUESTS</div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2, letterSpacing: 2 }}>ANNUAL LEAVE · ABSENCE MANAGEMENT</div>
        </div>
        <button style={S.btn("#C41E3A")} onClick={() => setShowAdd(true)}>+ Request Leave</button>
      </div>

      {pending > 0 && isAdmin && (
        <div style={{ background: "#F59E0B18", border: "1px solid #F59E0B", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#F59E0B" }}>{pending} pending request{pending > 1 ? "s" : ""} awaiting review</div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
        {[["Total", requests.length, "#1E3A5F"], ["Pending", requests.filter(r=>r.status==="pending").length, "#F59E0B"], ["Approved", requests.filter(r=>r.status==="approved").length, "#22C55E"]].map(([l,v,c]) => (
          <div key={l} style={{ background: "#0A1628", border: `1px solid ${c}33`, borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ fontSize: 32, fontWeight: 900, color: c, lineHeight: 1 }}>{v}</div>
            <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 2, textTransform: "uppercase", marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Loading...</div>
      ) : requests.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 40, color: "#9CA3AF" }}>No leave requests yet. Click "+ Request Leave" to submit one.</div>
      ) : (
        requests.map(r => (
          <div key={r.id} style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{r.officer_name || r.officer_email}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{r.type}</div>
              </div>
              <span style={S.badge(r.status)}>{STATUS[r.status]?.label}</span>
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 12, color: "#9CA3AF" }}>
              <span>📅 {fmt(r.from?.toDate ? r.from.toDate() : r.from)} → {fmt(r.to?.toDate ? r.to.toDate() : r.to)}</span>
              <span>🗓 {days(r.from, r.to)} day{days(r.from, r.to) !== 1 ? "s" : ""}</span>
            </div>
            {r.reason && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 8 }}>"{r.reason}"</div>}
            {isAdmin && r.status === "pending" && (
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button style={{ ...S.btn("#22C55E"), fontSize: 11, padding: "5px 14px" }} onClick={() => updateStatus(r.id, "approved")}>✓ Approve</button>
                <button style={{ ...S.btn("#EF4444", true), fontSize: 11, padding: "5px 14px" }} onClick={() => updateStatus(r.id, "denied")}>✗ Deny</button>
              </div>
            )}
          </div>
        ))
      )}

      {showAdd && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#060B14ee", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#0A1628", border: "1px solid #C41E3A", borderRadius: 12, padding: 28, width: 440 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Request Leave</div>
              <button onClick={() => setShowAdd(false)} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            {[["Leave Type", <select style={S.sel} value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}>{LEAVE_TYPES.map(t => <option key={t}>{t}</option>)}</select>],
              ["From Date", <input type="date" style={S.inp} value={form.from} onChange={e => setForm(f => ({...f, from: e.target.value}))} />],
              ["To Date", <input type="date" style={S.inp} value={form.to} onChange={e => setForm(f => ({...f, to: e.target.value}))} />],
              ["Reason (optional)", <input type="text" style={S.inp} placeholder="Brief reason..." value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))} />],
            ].map(([label, input]) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 2, display: "block", marginBottom: 6, textTransform: "uppercase" }}>{label}</label>
                {input}
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button style={{ ...S.btn("#1E3A5F", true), flex: 1 }} onClick={() => setShowAdd(false)}>Cancel</button>
              <button style={{ ...S.btn("#C41E3A"), flex: 2 }} onClick={submitRequest} disabled={submitting}>{submitting ? "Submitting..." : "Submit Request"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}